import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  DocumentIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { prepareHtmlSlideWithMermaid } from '../learning/htmlSlideMermaid';
import FreehandWhiteboard, {
  FreehandWhiteboardHandle,
} from '../learning/FreehandWhiteboard';
import apiClient from '../../services/apiClient';

export type WhiteboardEditRequest = {
  whiteboardId: number;
  materialId?: number;
  workshopId?: number;
  title: string;
  sceneJson: string;
  diagramId: string;
};

export interface DocumentViewerProps {
  fileUrl: string;
  fileType: string;
  title: string;
  /** When set with canAnnotate, Admin can open whiteboard */
  materialId?: number;
  workshopId?: number;
  courseId?: number;
  canAnnotate?: boolean;
  /** Called after a whiteboard is saved (refresh lists) */
  onWhiteboardSaved?: () => void;
  /** Open whiteboard with an existing Excalidraw scene (admin re-edit) */
  editRequest?: WhiteboardEditRequest | null;
  onEditRequestHandled?: () => void;
  /** Shrink iframe min-height so sibling whiteboard list stays visible */
  compact?: boolean;
}

function defaultWhiteboardTitle(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `Bảng ${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  fileUrl,
  fileType,
  title,
  materialId,
  workshopId,
  courseId,
  canAnnotate = false,
  onWhiteboardSaved,
  editRequest = null,
  onEditRequestHandled,
  compact = false,
}) => {
  const isHtml = fileType === 'html' || fileType === 'htm';
  const isPptx = fileType === 'pptx' || fileType === 'ppt';
  const isPdf = fileType === 'pdf';

  const containerRef = useRef<HTMLDivElement>(null);
  const freehandRef = useRef<FreehandWhiteboardHandle>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [htmlSrcDoc, setHtmlSrcDoc] = useState<string | null>(null);
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [htmlError, setHtmlError] = useState<string | null>(null);

  const [wbOpen, setWbOpen] = useState(false);
  const [wbDirty, setWbDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [savingWb, setSavingWb] = useState(false);
  const [wbTitle, setWbTitle] = useState(defaultWhiteboardTitle());
  const [wbSceneJson, setWbSceneJson] = useState<string | null>(null);
  const [wbSceneKey, setWbSceneKey] = useState('new');
  const [editingWhiteboardId, setEditingWhiteboardId] = useState<number | null>(null);

  // Admin + (material id or workshop id) → always show board entry
  const showAnnotate = Boolean(canAnnotate && (materialId || workshopId));

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsEl = document.fullscreenElement;
      const inOur = !!containerRef.current && fsEl === containerRef.current;
      setIsFullscreen(inOur);
      if (!inOur && wbOpen && !wbDirty) {
        setWbOpen(false);
        setConfirmClose(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [wbOpen, wbDirty]);

  useEffect(() => {
    if (!isHtml || !fileUrl) {
      setHtmlSrcDoc(null);
      setHtmlError(null);
      setHtmlLoading(false);
      return;
    }

    let cancelled = false;
    setHtmlLoading(true);
    setHtmlError(null);
    setHtmlSrcDoc(null);

    (async () => {
      try {
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error(`Không tải được slide (${res.status})`);
        const raw = await res.text();
        if (cancelled) return;
        setHtmlSrcDoc(prepareHtmlSlideWithMermaid(raw, fileUrl));
      } catch (e) {
        if (cancelled) return;
        setHtmlError(e instanceof Error ? e.message : 'Không tải được slide HTML');
        setHtmlSrcDoc(null);
      } finally {
        if (!cancelled) setHtmlLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isHtml, fileUrl]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  const openWhiteboard = useCallback(() => {
    setWbTitle(defaultWhiteboardTitle());
    setWbSceneJson(null);
    setWbSceneKey(`new-${Date.now()}`);
    setEditingWhiteboardId(null);
    setWbDirty(false);
    setConfirmClose(false);
    setWbOpen(true);
  }, []);

  const closeWhiteboardClean = useCallback(() => {
    setWbOpen(false);
    setConfirmClose(false);
    setWbDirty(false);
    setWbSceneJson(null);
    setEditingWhiteboardId(null);
  }, []);

  useEffect(() => {
    if (!editRequest || !showAnnotate) return;
    if (materialId && editRequest.materialId && editRequest.materialId !== materialId) return;
    if (workshopId && editRequest.workshopId && editRequest.workshopId !== workshopId) return;
    setWbTitle(editRequest.title || defaultWhiteboardTitle());
    setWbSceneJson(editRequest.sceneJson);
    setWbSceneKey(`edit-${editRequest.whiteboardId}-${editRequest.diagramId}`);
    setEditingWhiteboardId(editRequest.whiteboardId);
    setWbDirty(false);
    setConfirmClose(false);
    setWbOpen(true);
    onEditRequestHandled?.();
  }, [editRequest, showAnnotate, materialId, onEditRequestHandled]);

  const handleRequestCloseWhiteboard = useCallback(() => {
    if (wbDirty || freehandRef.current?.isDirty()) {
      setConfirmClose(true);
      return;
    }
    closeWhiteboardClean();
  }, [closeWhiteboardClean, wbDirty]);

  const handleDiscard = () => {
    closeWhiteboardClean();
  };

  const handleSaveAndClose = async () => {
    if (!materialId && !workshopId) {
      toast.error('Thiếu materialId hoặc workshopId — không lưu được');
      return;
    }
    try {
      setSavingWb(true);
      const payload = await freehandRef.current?.save();
      if (!payload) throw new Error('Không lấy được nội dung bảng trắng');

      const form = new FormData();
      form.append('diagramId', payload.diagramId);
      form.append('png', payload.pngBlob, `${payload.diagramId}.png`);
      form.append(
        'xml',
        new Blob([payload.strokesJson], { type: 'application/json' }),
        `${payload.diagramId}.json`
      );

      const up = await apiClient.post('/admin/uploads/diagram', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const data = up.data?.data;
      if (!data?.pngUrl || !data?.xmlUrl) {
        throw new Error(up.data?.message || 'Upload thất bại');
      }

      const diagramId = data.diagramId || payload.diagramId;
      const titleToSave = payload.title || wbTitle;

      if (editingWhiteboardId) {
        if (workshopId) {
          await apiClient.patch(`/admin/workshops/${workshopId}/whiteboards/${editingWhiteboardId}`, {
            title: titleToSave,
            diagram_id: diagramId,
            png_url: data.pngUrl,
            xml_url: data.xmlUrl,
            is_published: true,
          });
        } else {
          await apiClient.patch(`/admin/whiteboards/${editingWhiteboardId}`, {
            title: titleToSave,
            diagramId,
            pngUrl: data.pngUrl,
            xmlUrl: data.xmlUrl,
          });
        }
        toast.success('Đã cập nhật bảng trắng');
      } else {
        if (workshopId) {
          await apiClient.post(`/admin/workshops/${workshopId}/whiteboards`, {
            title: titleToSave,
            diagram_id: diagramId,
            png_url: data.pngUrl,
            xml_url: data.xmlUrl,
            is_published: true,
          });
        } else {
          await apiClient.post(`/admin/materials/${materialId}/whiteboards`, {
            title: titleToSave,
            diagramId,
            pngUrl: data.pngUrl,
            xmlUrl: data.xmlUrl,
            isPublished: true,
          });
        }
        toast.success('Đã lưu bảng trắng');
      }

      onWhiteboardSaved?.();
      closeWhiteboardClean();
    } catch (e: any) {
      toast.error(e?.message || e?.response?.data?.message || 'Lưu thất bại');
    } finally {
      setSavingWb(false);
      setConfirmClose(false);
    }
  };

  const downloadFilename = () => {
    const ext = (fileType || '').replace(/^\./, '').toLowerCase();
    const base = (title || 'tai-lieu').trim() || 'tai-lieu';
    if (ext && !base.toLowerCase().endsWith(`.${ext}`)) return `${base}.${ext}`;
    return base;
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      setDownloading(true);
      const res = await fetch(fileUrl);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(fileUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${window.location.origin}${fileUrl}`;

  const whiteboardChrome = showAnnotate && (
    <>
      {/* Always-visible floating button (not only fullscreen) */}
      <button
        type="button"
        onClick={() => {
          if (wbOpen) handleRequestCloseWhiteboard();
          else openWhiteboard();
        }}
        className="absolute z-[45] bottom-5 right-5 flex items-center gap-2 rounded-full bg-amber-500 text-white shadow-xl px-4 py-3 hover:bg-amber-600 transition-colors ring-2 ring-white"
        title={wbOpen ? 'Đóng bảng trắng' : 'Mở bảng trắng — viết / vẽ tự do'}
        aria-label={wbOpen ? 'Đóng bảng trắng' : 'Mở bảng trắng'}
      >
        {wbOpen ? <XMarkIcon className="w-6 h-6" /> : <PencilSquareIcon className="w-6 h-6" />}
        <span className="text-sm font-bold">
          {wbOpen ? 'Đóng bảng' : 'Bảng trắng'}
        </span>
      </button>

      <FreehandWhiteboard
        ref={freehandRef}
        open={wbOpen}
        title={wbTitle}
        onTitleChange={setWbTitle}
        onDirtyChange={setWbDirty}
        onRequestClose={handleRequestCloseWhiteboard}
        initialStrokesJson={wbSceneJson}
        sceneKey={wbSceneKey}
      />

      {confirmClose && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
            <h4 className="text-lg font-semibold text-m2-on-surface">Lưu bảng trắng?</h4>
            <p className="text-sm text-m2-on-surface-variant">
              Lưu phần viết/vẽ vừa rồi để học viên xem lại sau buổi học?
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg border border-m2-outline-variant hover:bg-m2-surface-low"
                onClick={() => setConfirmClose(false)}
                disabled={savingWb}
              >
                Tiếp tục vẽ
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                onClick={handleDiscard}
                disabled={savingWb}
              >
                Không lưu
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg bg-m2-primary text-white hover:bg-eh-forest-shadow disabled:opacity-50"
                onClick={() => void handleSaveAndClose()}
                disabled={savingWb}
              >
                {savingWb ? 'Đang lưu…' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const toolbar = (
    <div className="flex items-center space-x-3 shrink-0">
      {showAnnotate && (
        <button
          type="button"
          onClick={() => openWhiteboard()}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 shadow-sm"
        >
          <PencilSquareIcon className="h-4 w-4" />
          Bảng trắng
        </button>
      )}
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="inline-flex items-center gap-1 text-sm text-m2-primary hover:underline disabled:opacity-50"
      >
        <ArrowDownTrayIcon className="h-4 w-4" />
        {downloading ? 'Đang tải...' : 'Tải xuống'}
      </button>
      {(isHtml || isPdf || isPptx) && (
        <button
          onClick={toggleFullscreen}
          className="text-m2-on-surface-variant hover:text-m2-primary transition-colors"
          title="Toàn màn hình"
        >
          {isFullscreen ? (
            <ArrowsPointingInIcon className="w-5 h-5" />
          ) : (
            <ArrowsPointingOutIcon className="w-5 h-5" />
          )}
        </button>
      )}
    </div>
  );

  void courseId;
  void isFullscreen;
  const frameMinH = compact ? 'min-h-0 h-full flex-grow' : 'min-h-[600px]';
  const errorFrameMinH = compact ? 'min-h-0 h-full flex-grow' : 'min-h-[480px]';
  const fallbackMinH = compact ? 'min-h-0' : 'min-h-[400px]';

  if (isHtml) {
    return (
      <div
        ref={containerRef}
        className="relative w-full h-full flex flex-col bg-m2-surface-container rounded shadow-sm border border-m2-outline-variant"
      >
        <div className="p-4 border-b border-m2-outline-variant bg-m2-surface-low flex justify-between items-center shrink-0 gap-3">
          <h3 className="font-semibold text-m2-on-surface line-clamp-1 min-w-0">{title}</h3>
          <div className="flex items-center space-x-3 shrink-0">
            <a href={fileUrl} target="_blank" rel="noreferrer" className="text-m2-primary hover:underline text-sm">
              Mở tab mới
            </a>
            {toolbar}
          </div>
        </div>
        {htmlLoading && (
          <div className={`flex-grow flex items-center justify-center ${frameMinH} text-sm text-m2-on-surface-variant`}>
            Đang tải slide…
          </div>
        )}
        {!htmlLoading && htmlError && (
          <div className={`flex-grow flex flex-col ${errorFrameMinH}`}>
            <iframe
              src={fileUrl}
              className={`w-full flex-grow border-none ${errorFrameMinH}`}
              title={title}
              sandbox="allow-scripts allow-same-origin"
              onLoad={(e) => (e.target as HTMLIFrameElement).focus()}
            />
          </div>
        )}
        {!htmlLoading && !htmlError && htmlSrcDoc && (
          <iframe
            srcDoc={htmlSrcDoc}
            className={`w-full flex-grow border-none ${frameMinH}`}
            title={title}
            sandbox="allow-scripts allow-same-origin allow-popups"
            onLoad={(e) => (e.target as HTMLIFrameElement).focus()}
          />
        )}
        {whiteboardChrome}
      </div>
    );
  }

  if (isPptx) {
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fullUrl)}`;
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    return (
      <div
        ref={containerRef}
        className="relative w-full h-full flex flex-col bg-m2-surface-container rounded shadow-sm border border-m2-outline-variant"
      >
        <div className="p-4 border-b border-m2-outline-variant bg-m2-surface-low flex justify-between items-center shrink-0 gap-3">
          <h3 className="font-semibold text-m2-on-surface line-clamp-1 min-w-0">{title}</h3>
          {toolbar}
        </div>

        {isLocalhost ? (
          <div className={`flex-grow flex flex-col items-center justify-center ${frameMinH} bg-m2-surface-low p-8 text-center`}>
            <DocumentIcon className="w-16 h-16 mb-4 text-orange-400" />
            <h4 className="text-xl font-medium text-m2-on-surface mb-2">
              Không thể xem trực tiếp PPTX trên Localhost
            </h4>
            <p className="text-m2-on-surface-variant max-w-lg mb-6">
              Trình xem tài liệu Microsoft yêu cầu đường dẫn (URL) của file phải được công khai trên
              Internet. Vì bạn đang chạy web trên <b>localhost</b>, tính năng này tạm thời không hoạt
              động. Tính năng sẽ hoạt động bình thường khi web được deploy lên một tên miền thật.
            </p>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="px-6 py-2 bg-m2-primary text-m2-on-primary rounded-lg hover:bg-eh-forest-shadow transition-colors shadow-sm disabled:opacity-50"
            >
              {downloading ? 'Đang tải...' : 'Tải file xuống máy tính'}
            </button>
          </div>
        ) : (
          <iframe src={viewerUrl} className={`w-full flex-grow border-none ${frameMinH}`} title={title} />
        )}
        {whiteboardChrome}
      </div>
    );
  }

  if (isPdf) {
    return (
      <div
        ref={containerRef}
        className="relative w-full h-full flex flex-col bg-m2-surface-container rounded shadow-sm border border-m2-outline-variant"
      >
        <div className="p-4 border-b border-m2-outline-variant bg-m2-surface-low flex justify-between items-center shrink-0 gap-3">
          <h3 className="font-semibold text-m2-on-surface line-clamp-1 min-w-0">{title}</h3>
          <div className="flex items-center space-x-3 shrink-0">
            <a href={fileUrl} target="_blank" rel="noreferrer" className="text-m2-primary hover:underline text-sm">
              Mở tab mới
            </a>
            {toolbar}
          </div>
        </div>
        <iframe src={fileUrl} className={`w-full flex-grow border-none ${frameMinH}`} title={title} />
        {whiteboardChrome}
      </div>
    );
  }

  // Unsupported type but can still whiteboard if admin teaching from material
  if (showAnnotate) {
    return (
      <div
        ref={containerRef}
        className={`relative w-full h-full ${fallbackMinH} flex flex-col bg-m2-surface-container rounded shadow-sm border border-m2-outline-variant`}
      >
        <div className="p-4 border-b border-m2-outline-variant bg-m2-surface-low flex justify-between items-center shrink-0 gap-3">
          <h3 className="font-semibold text-m2-on-surface line-clamp-1 min-w-0">{title}</h3>
          {toolbar}
        </div>
        <div className="flex-grow flex flex-col items-center justify-center text-m2-on-surface-variant p-8">
          <DocumentIcon className="w-16 h-16 mb-4 text-m2-outline-variant" />
          <p className="text-sm mb-4">Không xem trực tiếp được file này — vẫn có thể mở bảng trắng.</p>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="px-4 py-2 bg-m2-primary text-m2-on-primary rounded hover:bg-eh-forest-shadow disabled:opacity-50"
          >
            {downloading ? 'Đang tải...' : 'Tải xuống'}
          </button>
        </div>
        {whiteboardChrome}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-[400px] bg-m2-surface-low rounded border border-dashed border-m2-outline text-m2-on-surface-variant flex-col">
      <DocumentIcon className="w-16 h-16 mb-4 text-m2-outline-variant" />
      <p className="text-lg font-medium text-m2-on-surface mb-2">{title}</p>
      <p className="text-sm mb-4">Loại file này không hỗ trợ xem trực tiếp trên trình duyệt</p>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="px-4 py-2 bg-m2-primary text-m2-on-primary rounded hover:bg-eh-forest-shadow transition-colors disabled:opacity-50"
      >
        {downloading ? 'Đang tải...' : 'Tải xuống'}
      </button>
    </div>
  );
};

export default DocumentViewer;
